import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { Box, Button, Divider, List, ListItemButton, ListItemText, Tab, Tabs, Typography } from '@mui/material';
import { castDraft, produce } from 'immer';

import {
  AnalysisSettings,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSelection,
  MaterialSettings,
  OrganizationPreset,
  ProfileDefaults,
} from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { getLoggedInUsersOrganizationMaterialSet } from '@paragon/material-lists';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { createScrollbarStyling } from '@paragon/themes';
import { assertExhaustiveSwitch, humanCase } from '@paragon/utilities';

import { showProjectWideSettingsDialog } from '../../../store/actions/popupActions';
import { getUniqueLumbersInLumberList } from '../../../store/selectors/getUniqueLumbersInLumberList';
import { useAppSelector } from '../../hooks';
import AnalysisSettingsControls from '../../interface/subpanels/settings/analysis/AnalysisSettingsControls';
import { FeaturePatternSettingsControls } from '../../interface/subpanels/settings/feature-pattern/FeaturePatternSettingsControls';
import { LoadSettingsControls } from '../../interface/subpanels/settings/loading/LoadSettingsControls';
import { MaterialSelectionControls } from '../../interface/subpanels/settings/material/MaterialSelectionControls';
import MaterialSettingsLumbers from '../../interface/subpanels/settings/material/MaterialSettingsLumbers';
import { ProfileDefaultsControls } from '../../interface/subpanels/settings/profile-defaults/ProfileDefaultsControls';
import { LoadingSettingsDialog } from '../project-wide-settings/LoadingSettingsDialog';
import { SettingsErrorDialog } from '../project-wide-settings/SettingsErrorDialog';
import { useFetchedPresets } from '../project-wide-settings/useFetchedPresets';
import { DeletePresetDialog } from './DeletePresetDialog';
import {
  getAllSettingsTypesSupportingPresets,
  SettingsOfType,
  settingsPresetsOfType,
  SettingsPresetType,
} from './presetTypes';
import { RenamePresetDialog } from './RenamePresetDialog';
import { UnsavedChangesInfoDialog } from './UnsavedChangesInfoDialog';
import { useFlash } from './useFlash';

const MAX_CONTENT_HEIGHT = '70vh';

export function PresetsDialog() {
  const dispatch = useDispatch();
  const materialSet = useAppSelector(getLoggedInUsersOrganizationMaterialSet);
  const isInProjectContext = useAppSelector((state) => state.application.openContext?.type === 'project');

  const [flash, setFlash, clearFlash] = useFlash();

  const [isRenaming, setIsRenaming] = useState(false);
  const [isConfirmingDeletion, setIsConfirmingDeletion] = useState(false);
  const [showingUnsavedChangesWarningFor, setShowingUnsavedChangesWarningFor] = useState<string | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [savingDidError, setSavingDidError] = useState(false);

  const [activeSettingsType, setActiveSettingsType] = useState<SettingsPresetType>('loadSettings');

  const { getPresetsOfType, invalidate } = useFetchedPresets('all');

  const [_selectedPresetGuid, _setSelectedPresetGuid] = useState<string | null>(null);
  const [updatedSelectedPreset, setUpdatedSelectedPreset] = useState<OrganizationPreset<
    SettingsOfType<SettingsPresetType>
  > | null>(null);
  const [updatedMaterialSelection, setUpdatedMaterialSelection] = useState<MaterialSelection | null>(null);

  function setSelectedPresetGuid(guid: string) {
    if (hasUnsavedChanges()) {
      setShowingUnsavedChangesWarningFor('changing presets');
    } else {
      clearFlash();
      _setSelectedPresetGuid(guid);
      setUpdatedSelectedPreset(null);
      setUpdatedMaterialSelection(null);
    }
  }

  function hasUnsavedChanges(): boolean {
    return !!(updatedSelectedPreset || updatedMaterialSelection);
  }

  const settingsTypes = getAllSettingsTypesSupportingPresets();
  const isLoading = settingsTypes.some((key) => {
    const presets = getPresetsOfType(key);
    return presets === undefined || presets === 'fetching';
  });
  const hasError = settingsTypes.some((key) => getPresetsOfType(key) === 'error');

  if (isLoading) return <LoadingSettingsDialog />;
  if (hasError)
    return (
      <SettingsErrorDialog>
        There was an error loading your settings presets. Try closing and re-opening the dialog, or contact Paragon
        Support if the error persists.
      </SettingsErrorDialog>
    );

  const currentPresets = getPresetsOfType(activeSettingsType);

  const selectedPresetGuid =
    _selectedPresetGuid ?? (Array.isArray(currentPresets) && currentPresets.length > 0 ? currentPresets[0].guid : null);

  const selectedPreset =
    Array.isArray(currentPresets) && selectedPresetGuid
      ? currentPresets.find((preset) => preset.guid === selectedPresetGuid)
      : null;

  /**
   * Returns `undefined` if selected preset is of a type where material selection is irrelevant. Otherwise, returns the
   *   current material selection or throws, since a material selection is required for settings types that have it.
   */
  function getCurrentMaterialSelection(): MaterialSelection | undefined {
    if (selectedPreset == null || !settingsPresetsOfType(activeSettingsType).hasMaterialSelection) return undefined;

    if (updatedMaterialSelection) return updatedMaterialSelection;

    if (selectedPreset.lumberListGuid && selectedPreset.plateListGuid && selectedPreset.hangerListGuid) {
      return {
        lumberListGuid: selectedPreset.lumberListGuid,
        plateListGuid: selectedPreset.plateListGuid,
        hangerListGuid: selectedPreset.hangerListGuid,
      };
    }

    throw new Error('Invalid preset encountered -- missing material selection when required.');
  }

  /**
   * The variant of `getCurrentMaterialSelection` intended for use in cases where a material selection must exist.
   */
  function getCurrentMaterialSelectionOrThrow() {
    const result = getCurrentMaterialSelection();
    if (result == null) {
      throw new Error('Material selection requested when not expected.');
    }

    return result;
  }

  return (
    <>
      <Dialog
        insistent
        variant="design"
        maxWidth="lg"
        contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
        submitDisabled={(updatedSelectedPreset == null && updatedMaterialSelection == null) || isSaving}
        submitText={isSaving ? 'Saving...' : 'Save'}
        onSubmit={() => {
          if (selectedPreset == null || (updatedSelectedPreset == null && updatedMaterialSelection == null))
            throw new Error('Invalid state -- null selected preset was saved');

          setIsSaving(true);

          const presetType = settingsPresetsOfType(activeSettingsType);
          const materialSelection = presetType.hasMaterialSelection
            ? (updatedMaterialSelection ?? getCurrentMaterialSelection())
            : undefined;

          presetType
            .update(
              selectedPreset.guid,
              (updatedSelectedPreset ?? selectedPreset).name,
              (updatedSelectedPreset ?? selectedPreset).settings,
              materialSelection,
            )
            .then(() => invalidate('all', { suppressFetchingState: true }))
            .then(() => {
              setSavingDidError(false);
              setUpdatedSelectedPreset(null);
              setUpdatedMaterialSelection(null);

              setFlash({ type: 'success', message: 'Preset saved successfully.' });
            })

            .catch((error) => {
              console.error(error);
              setSavingDidError(true);
            })
            .finally(() => setIsSaving(false));
        }}
        actionsNode={
          <>
            <Box sx={{ marginLeft: selectedPreset ? '300px' : undefined, marginRight: 'auto' }}>
              {savingDidError ? (
                <Typography color="red">Error saving preset. Try again or contact Paragon Support.</Typography>
              ) : (activeSettingsType === 'loadSettings' || activeSettingsType === 'analysisSettings') &&
                isInProjectContext ? (
                <Button
                  variant="contained"
                  startIcon={<SettingsIcon />}
                  onClick={() => {
                    dispatch(closeCurrentPopup());
                    dispatch(showProjectWideSettingsDialog(activeSettingsType, []));
                  }}
                >
                  Go To Project Settings
                </Button>
              ) : null}
            </Box>

            {flash && <Typography color={flash.type}>{flash.message}</Typography>}

            <Button
              variant="outlined"
              disabled={isSaving}
              onClick={() => {
                if (hasUnsavedChanges()) {
                  setShowingUnsavedChangesWarningFor('closing this dialog');
                } else {
                  dispatch(closeCurrentPopup());
                }
              }}
            >
              Close
            </Button>

            <Button
              variant="outlined"
              disabled={(updatedSelectedPreset == null && updatedMaterialSelection == null) || isSaving}
              onClick={() => {
                setUpdatedSelectedPreset(null);
                setUpdatedMaterialSelection(null);
              }}
            >
              Reset Changes
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 1 }}>
          <Tabs
            value={activeSettingsType}
            onChange={(_, tabValue) => {
              if (hasUnsavedChanges()) {
                setShowingUnsavedChangesWarningFor('switching tabs');
              } else {
                clearFlash();
                _setSelectedPresetGuid(null);
                setUpdatedSelectedPreset(null);
                setUpdatedMaterialSelection(null);
                setActiveSettingsType(tabValue);
              }
            }}
          >
            {getAllSettingsTypesSupportingPresets().map((key) => (
              <Tab key={key} value={key} label={settingsPresetsOfType(key).label} />
            ))}
          </Tabs>
        </Box>

        {selectedPreset == null ? (
          <NoPresetsPlaceholder userVisibleType={humanCase(activeSettingsType)} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
            <PresetsDialogSidebar
              selectedPresetGuid={selectedPresetGuid}
              presets={Array.isArray(currentPresets) ? currentPresets : []}
              onSelectPreset={setSelectedPresetGuid}
              onRenameSelectedPreset={() => void setIsRenaming(true)}
              onDeleteSelectedPreset={() => void setIsConfirmingDeletion(true)}
            />

            <Divider orientation="vertical" flexItem />

            <Box sx={[{ flex: 1, overflowY: 'auto', maxHeight: MAX_CONTENT_HEIGHT }, createScrollbarStyling]}>
              {(() => {
                switch (activeSettingsType) {
                  case 'loadSettings':
                    return (
                      <LoadSettingsControls
                        showHeader={false}
                        showPresetControls={false}
                        forceComponentType="NonComponent" // show both floor + roof settings
                        isViewOnly={isSaving}
                        loadSettingsArray={[
                          (updatedSelectedPreset?.settings ?? selectedPreset.settings) as LoadSettings,
                        ]}
                        onChange={(changes) => {
                          if (selectedPreset == null)
                            throw new Error('Invalid state -- null selected preset was updated');
                          setUpdatedSelectedPreset(
                            produce(selectedPreset, (draft) => {
                              Object.assign(draft.settings, changes);
                            }),
                          );
                        }}
                        designCodeElement={null}
                      />
                    );

                  case 'analysisSettings':
                    return (
                      <AnalysisSettingsControls
                        showHeader={false}
                        showPresetControls={false}
                        isViewOnly={isSaving}
                        analysisSettingsArray={[
                          (updatedSelectedPreset?.settings ?? selectedPreset.settings) as AnalysisSettings,
                        ]}
                        onChange={(updater) => {
                          if (selectedPreset == null)
                            throw new Error('Invalid state -- null selected preset was updated');
                          setUpdatedSelectedPreset(
                            produce(selectedPreset, (draft) => {
                              draft.settings = updater(draft.settings as AnalysisSettings);
                            }),
                          );
                        }}
                      />
                    );

                  case 'materialSettings':
                    return (
                      <Box sx={{ minWidth: 750 }}>
                        <MaterialSelectionControls
                          materialSelection={getCurrentMaterialSelectionOrThrow()}
                          onMaterialSelectionChange={setUpdatedMaterialSelection}
                          featurePatternMaterialSettings={
                            (updatedSelectedPreset?.settings ?? selectedPreset.settings) as MaterialSettings
                          }
                          onFeaturePatternMaterialSettingsChange={(settings) => {
                            if (selectedPreset == null)
                              throw new Error('Invalid state -- null selected preset was updated');
                            setUpdatedSelectedPreset(
                              produce(selectedPreset, (draft) => {
                                draft.settings = castDraft(settings);
                              }),
                            );
                          }}
                          disabled={isSaving}
                        />

                        <Divider sx={{ my: 2 }} />

                        <MaterialSettingsLumbers
                          mode="all"
                          disabled={isSaving}
                          inventoryLumbers={getUniqueLumbersInLumberList(
                            materialSet,
                            getCurrentMaterialSelectionOrThrow().lumberListGuid,
                          )}
                          settings={(updatedSelectedPreset?.settings ?? selectedPreset.settings) as MaterialSettings}
                          onSettingsChange={(settings) => {
                            if (selectedPreset == null)
                              throw new Error('Invalid state -- null selected preset was updated');
                            setUpdatedSelectedPreset(
                              produce(selectedPreset, (draft) => {
                                draft.settings = castDraft(settings);
                              }),
                            );
                          }}
                        />
                      </Box>
                    );

                  case 'featurePatternTrussSettings':
                    return (
                      <FeaturePatternSettingsControls
                        isViewOnly={isSaving}
                        showHeader={false}
                        showPresetControls={false}
                        settingsObject={
                          (updatedSelectedPreset?.settings ?? selectedPreset.settings) as FeaturePatternTrussSettings
                        }
                        onChange={(changes) => {
                          if (selectedPreset == null)
                            throw new Error('Invalid state -- null selected preset was updated');
                          setUpdatedSelectedPreset(
                            produce(selectedPreset, (draft) => {
                              Object.assign(draft.settings, changes);
                            }),
                          );
                        }}
                      />
                    );

                  case 'profileDefaults':
                    return (
                      <ProfileDefaultsControls
                        isViewOnly={isSaving}
                        showHeader={false}
                        showPresetControls={false}
                        settingsObject={(updatedSelectedPreset?.settings ?? selectedPreset.settings) as ProfileDefaults}
                        onChange={(changes) => {
                          if (selectedPreset == null)
                            throw new Error('Invalid state -- null selected preset was updated');
                          setUpdatedSelectedPreset(
                            produce(selectedPreset, (draft) => {
                              Object.assign(draft.settings, changes);
                            }),
                          );
                        }}
                      />
                    );

                  default:
                    assertExhaustiveSwitch(activeSettingsType);
                }
              })()}
            </Box>
          </Box>
        )}
      </Dialog>

      {isRenaming ? (
        <RenamePresetDialog
          name={selectedPreset!.name}
          onClose={() => setIsRenaming(false)}
          onRename={async (name) => {
            if (selectedPreset == null) throw new Error('Invalid state -- null selected preset was renamed');

            await settingsPresetsOfType(activeSettingsType).update(
              selectedPreset.guid,
              name,
              selectedPreset.settings,
              getCurrentMaterialSelection(),
            );
            await invalidate('all', { suppressFetchingState: true });
          }}
        />
      ) : isConfirmingDeletion ? (
        <DeletePresetDialog
          onClose={() => {
            setIsConfirmingDeletion(false);
          }}
          onDeleteConfirmed={async () => {
            if (selectedPresetGuid == null) throw new Error('Invalid state -- null selected preset guid was deleted');

            await designService.presets
              .deletePreset(selectedPresetGuid)
              .then(() => invalidate('all', { suppressFetchingState: true }))
              .then(() => {
                clearFlash();
                _setSelectedPresetGuid(null);
                setUpdatedSelectedPreset(null);
              })
              .catch((error) => {
                console.error(error);

                setFlash({
                  type: 'error',
                  message: 'Unable to delete preset. Please try again or contact Paragon Customer Support.',
                });
              });
          }}
        />
      ) : null}

      {showingUnsavedChangesWarningFor && (
        <UnsavedChangesInfoDialog
          userVisibleAction={showingUnsavedChangesWarningFor}
          onClose={() => setShowingUnsavedChangesWarningFor(null)}
        />
      )}
    </>
  );
}

function NoPresetsPlaceholder(props: { userVisibleType: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: 'text.secondary',
        marginY: 2,
      }}
    >
      <SettingsSuggestIcon sx={{ fontSize: '96px' }} />
      <Typography sx={{ marginTop: 2, marginX: 10 }}>
        Your organization does not have any presets for {props.userVisibleType}. Presets allow you to easily apply the
        same exact settings across multiple projects. You can create new presets in the settings dialog or panel.
      </Typography>
    </Box>
  );
}

function PresetsDialogSidebar(props: {
  presets: OrganizationPreset<unknown>[];

  selectedPresetGuid: string | null;

  onSelectPreset(guid: string): void;

  onRenameSelectedPreset(): void;
  onDeleteSelectedPreset(): void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <List sx={[{ minWidth: 250, maxHeight: MAX_CONTENT_HEIGHT, overflowY: 'auto' }, createScrollbarStyling]}>
        {props.presets.length > 0 &&
          props.presets.map((preset) => (
            <ListItemButton
              selected={preset.guid === props.selectedPresetGuid}
              key={preset.guid}
              onClick={() => void props.onSelectPreset(preset.guid)}
            >
              <ListItemText primary={preset.name} />
            </ListItemButton>
          ))}
      </List>

      <Button
        variant="outlined"
        startIcon={<DriveFileRenameOutlineIcon />}
        disabled={props.selectedPresetGuid == null}
        onClick={() => void props.onRenameSelectedPreset()}
      >
        Rename Selected Preset
      </Button>

      <Button
        variant="outlined"
        startIcon={<DeleteIcon />}
        disabled={props.selectedPresetGuid == null}
        onClick={() => void props.onDeleteSelectedPreset()}
      >
        Delete Selected Preset
      </Button>
    </Box>
  );
}
